import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Transition from 'react-transition-group/Transition'
import { cx } from '@emotion/css'
import { Chip, colors } from '@offerzen/pinata'
import MenuIcon from '@offerzen/zenicon/MenuIcon'
import BlocksIcon from '@offerzen/zenicon/BlocksIcon'
import UserIcon from '@offerzen/zenicon/UserIcon'
import ConversationIcon from '@offerzen/zenicon/ConversationIcon'
import CalendarIcon from '@offerzen/zenicon/CalendarIcon'
import WarningCircleIcon from '@offerzen/zenicon/WarningCircleIcon'
import GearIcon from '@offerzen/zenicon/GearIcon'
import BriefcaseIcon from '@offerzen/zenicon/BriefcaseIcon'

import FeatureFlags from '@/CandidateExperience/constants/featureFlagConstants'
import FeatureFlag, { FeatureFlagEnabled } from '@/CandidateExperience/components/FeatureFlag'

import AccessibleIcon from '../AccessibleIcon'

import MobileLink from './MobileLink'
import styles, { wrapperTransition, navTransition } from './styles'

class MobileNavMenu extends Component {
  state = {
    open: false,
  }

  toggleNavbar = () => this.setState({ open: !this.state.open })

  isArchivedIRs = location =>
    location.pathname === `/candidate/${this.props.candidateSlug}/interview_requests` && location.search.includes('filter=archived')

  render() {
    const { open } = this.state
    const { candidateSlug, candidateState, unreadInterviewRequestCount, emailPreferencesAccessTokenKey, ...props } = this.props

    return (
      <>
        <div className={styles.menu} onClick={this.toggleNavbar}>
          <AccessibleIcon icon={MenuIcon} size={24} color={colors.white} />
        </div>

        <Transition in={open} timeout={{ enter: 0, exit: 300 }} mountOnEnter unmountOnExit>
          {status => (
            <div className={cx(styles.wrapper, wrapperTransition(status))}>
              <div className={styles.overlay} onClick={this.toggleNavbar} />

              <ul className={cx(styles.nav, navTransition(status))}>
                <li className={styles.navItem}>
                  <MobileLink exact to={`/candidate/${candidateSlug}/dashboard`} toggleNavbar={this.toggleNavbar}>
                    <AccessibleIcon className={styles.icon} icon={BlocksIcon} size={18} />
                    <span>Dashboard</span>
                  </MobileLink>
                </li>

                <li className={styles.navItem}>
                  <MobileLink exact to={`/candidate/${candidateSlug}`} toggleNavbar={this.toggleNavbar}>
                    <AccessibleIcon className={styles.icon} icon={UserIcon} size={18} />
                    <span>Profile</span>

                    <Chip
                      theme={candidateState === 'live' ? 'success' : 'error'}
                      style="dark"
                      label={candidateState === 'live' ? 'Live' : 'Hidden'}
                      className={styles.chip}
                    />
                  </MobileLink>
                </li>

                <FeatureFlag flag={FeatureFlags.CandidatePositionRecommendedApply}>
                  <FeatureFlagEnabled>
                    <li className={styles.navItem}>
                      <MobileLink exact to={`/candidate/${candidateSlug}/recommended-jobs`} toggleNavbar={this.toggleNavbar}>
                        <AccessibleIcon className={styles.icon} icon={BriefcaseIcon} size={18} />
                        <span>Jobs</span>
                      </MobileLink>
                    </li>
                  </FeatureFlagEnabled>
                </FeatureFlag>

                <If condition={!props.disabledCandidate}>
                  <li className={styles.navItem}>
                    <MobileLink to={`/candidate/${candidateSlug}/interview_requests`} toggleNavbar={this.toggleNavbar} isMenu>
                      <AccessibleIcon className={styles.icon} icon={ConversationIcon} size={18} />
                      <span>Interviews</span>

                      <If condition={unreadInterviewRequestCount > 0}>
                        <Chip theme="legacy" style="dark" label={unreadInterviewRequestCount} className={styles.chip} />
                      </If>
                    </MobileLink>

                    <ul className={styles.subNav}>
                      <li>
                        <MobileLink
                          to={`/candidate/${candidateSlug}/interview_requests`}
                          isActive={(_, location) => !this.isArchivedIRs(location)}
                          toggleNavbar={this.toggleNavbar}
                          isSubLink>
                          <span>Open</span>
                        </MobileLink>
                      </li>
                      <li>
                        <MobileLink
                          to={`/candidate/${candidateSlug}/interview_requests?filter=archived`}
                          isActive={(_, location) => this.isArchivedIRs(location)}
                          toggleNavbar={this.toggleNavbar}
                          isSubLink>
                          <span>Archived</span>
                        </MobileLink>
                      </li>
                    </ul>
                  </li>
                </If>

                <If condition={['ready_to_go_live', 'live', 'post_live'].includes(candidateState)}>
                  <li className={styles.navItem}>
                    <a href={`/candidate/${candidateSlug}/calendar`} className={styles.link}>
                      <AccessibleIcon className={styles.icon} icon={CalendarIcon} size={18} />
                      <span>Calendar</span>

                      <If condition={!props.candidateAvailabilitySet}>
                        <div className={styles.alertIcon}>
                          <AccessibleIcon className={styles.icon} icon={WarningCircleIcon} size={14} color={colors.mandy} />
                        </div>
                      </If>
                    </a>
                  </li>
                </If>

                <li className={styles.navItem}>
                  <a href={`/candidate/${candidateSlug}/account/details`} className={styles.link}>
                    <AccessibleIcon className={styles.icon} icon={GearIcon} size={18} />
                    <span>Account</span>
                  </a>

                  <ul className={styles.subNav}>
                    <li>
                      <a href={`/candidate/${candidateSlug}/account/details`} className={styles.subLink}>
                        <span>Your details</span>
                      </a>
                    </li>
                    <li>
                      <a href={`/candidate/${candidateSlug}/account/manage_data`} className={styles.subLink}>
                        <span>Manage your data</span>
                      </a>
                    </li>
                    <li>
                      <a href={`/email_preferences/${emailPreferencesAccessTokenKey}`} className={styles.subLink}>
                        <span>Email preferences</span>
                      </a>
                    </li>
                    <li>
                      <a href="/privacy-centre" className={styles.subLink} target="_blank">
                        <span>Privacy Centre</span>
                      </a>
                    </li>
                    <li>
                      <a rel="nofollow" tabIndex="0" onClick={props.signOut} className={styles.subLink}>
                        <span>Log out</span>
                      </a>
                    </li>
                  </ul>
                </li>

                <li>
                  <a href={`/candidate/${candidateSlug}/referrals?s=sidebar_nav`} className={styles.linkButton}>
                    <span>Invite Friends</span>
                  </a>
                </li>
              </ul>
            </div>
          )}
        </Transition>
      </>
    )
  }
}

MobileNavMenu.propTypes = {
  candidateSlug: PropTypes.string,
  candidateState: PropTypes.string,
  disabledCandidate: PropTypes.bool,
  unreadInterviewRequestCount: PropTypes.number,
  signOut: PropTypes.func,
  emailPreferencesAccessTokenKey: PropTypes.string,
}

export default MobileNavMenu
